import React, { useEffect, useReducer, useState } from 'react'
import { Stack } from '@mui/material'
import { PATH_PGSSE } from 'home/constants/RouteConstants'
import PgsseAction from 'pgsse/action/PgsseAction'
import { getUdiSectorName } from 'pgsse/util/PgsseUtil'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import DangerTable from './DangerTable'
import { compact, isNil, omit } from 'lodash'
import { REDUCER_DANGERS } from 'pgsse/constant/PgsseConstants'
import PGSSEInfos from '../common/PGSSEInfos'
import { usedUrl } from 'conf/basepath'
import Breadcrumb from '../common/BreadCrumb'
import ImportDangerDialog from './ImportDangerDialog'

const initialState = {
    addedDangers: [],
    updatedDangers: [],
    deletedDangers: [],
}

const reducer = (state, action) => {
    const { type, payload } = action
    switch (type) {
        case REDUCER_DANGERS.ADD:
            return {
                ...state,
                addedDangers: [ ...payload, ...state.addedDangers].map((d, indexAdd) => ({ ...d, indexAdd })),
            }
        case REDUCER_DANGERS.UPDATE:
            if (!isNil(payload.indexAdd)) {
                return {
                    ...state,
                    addedDangers: state.addedDangers.map(d => d.indexAdd === payload.indexAdd ? payload : d),
                }
            }
            return {
                ...state,
                updatedDangers: [
                    ...state.updatedDangers.filter(d => d.idDanger !== payload.idDanger),
                    payload,
                ],
            }
        case REDUCER_DANGERS.DELETE:
            return {
                ...state,
                addedDangers: state.addedDangers.filter(d => d.indexAdd !== payload.indexAdd),
                updatedDangers: state.updatedDangers.filter(d => d.idDanger !== payload.idDanger),
                deletedDangers: compact([ ...state.deletedDangers, payload.idDanger ]),
            }
        case REDUCER_DANGERS.RESET:
            return initialState
        default:
            throw new Error()
    }
}

const PgsseDangerApp = () => {
    const dispatch = useDispatch()
    const { idDiagnostic } = useParams()

    const {
        distributionUnits,
        sectors,
        diagnostic,
        accountUser,
    } = useSelector(store => ({
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        sectors: store.SectorReducer.sectors,
        diagnostic: store.PgsseReducer.diagnostic,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const [readMode, setReadMode] = useState(true)
    const [stateAUD, dispatchStateAUD] = useReducer(reducer, initialState)
    const [importDialogOpen, setImportDialogOpen] = useState(false)

    useEffect(() => {
        dispatch(PgsseAction.fetchDiagnostic(idDiagnostic))
        dispatch(PgsseAction.fetchDiagnosticDangers(idDiagnostic))
        dispatch(PgsseAction.fetchDiagnosticElements(idDiagnostic))
    }, [dispatch, idDiagnostic])

    useTitle(() => {
        const udi = distributionUnits.find(du => du.id === diagnostic.idUdi)
        const sector = sectors.find(s => s.id === diagnostic.idSector)
        return [{
            title: i18n.pgsse,
            href: PATH_PGSSE,
        }, {
            title: i18n.phase2,
            href: `${PATH_PGSSE}/phase2`,
        }, {
            title: getUdiSectorName(udi, sector),
            href: `${PATH_PGSSE}/phase2/${diagnostic.idDiag}`,
        }]
    }, [diagnostic])

    useActions(() => {
        const actions = readMode ? {
            edit: () => setReadMode(false),
            play: () => window.open(`${usedUrl}#/pgsse/diagnostic/${idDiagnostic}`, '_blank').focus(),
            import: () => setImportDialogOpen(true),
        } : {
            cancel: () => {
                dispatchStateAUD({ type: REDUCER_DANGERS.RESET })
                setReadMode(true)
            },
            save: () => {
                dispatch(PgsseAction.updateDangers(idDiagnostic, stateAUD)).then(() => {
                    dispatch(PgsseAction.fetchDiagnosticDangers(idDiagnostic))
                    dispatchStateAUD({ type: REDUCER_DANGERS.RESET })
                    setReadMode(true)
                })
            },
            play: () => window.open(`${usedUrl}#/pgsse/diagnostic/${idDiagnostic}`, '_blank').focus(),
        }
        return accountUser.consultant === '1' ? omit(actions, ['edit', 'save', 'cancel']) : actions
    }, [readMode, diagnostic, idDiagnostic, stateAUD, accountUser])

    return (
        <Stack spacing={1} sx={{ padding: '1rem' }}>
            <Breadcrumb/>
            <PGSSEInfos readMode diagnostic={diagnostic}/>
            <DangerTable readMode={readMode} stateAUD={stateAUD} dispatchStateAUD={dispatchStateAUD}/>
            <ImportDangerDialog open={importDialogOpen} setIsOpen={setImportDialogOpen} idDiag={parseInt(idDiagnostic)} phase={2}/>
        </Stack>
    )
}

export default PgsseDangerApp
